import React, { useState, useEffect } from 'react';

const CookieConsentForm = ({ isOpen, onClose }) => {
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Diese sind immer aktiviert
    functional: false,
    analytics: false,
    marketing: false
  });

  // Laden der gespeicherten Einstellungen beim ersten Rendern
  useEffect(() => {
    const savedSettings = localStorage.getItem('cookieSettings');
    if (savedSettings) {
      setCookieSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setCookieSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const saveSettings = () => {
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    // Hier könnte auch ein Event ausgelöst werden, auf das andere Komponenten reagieren können
    onClose();
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookieSettings', JSON.stringify(allAccepted));
    setCookieSettings(allAccepted);
    onClose();
  };

  const rejectAll = () => {
    const allRejected = {
      necessary: true, // Diese bleiben aktiviert
      functional: false,
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookieSettings', JSON.stringify(allRejected));
    setCookieSettings(allRejected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-serif text-gray-800">Cookie-Einstellungen</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
            Hier können Sie Ihre Cookie-Einstellungen verwalten.
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Notwendige Cookies */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="necessary"
                name="necessary"
                type="checkbox"
                checked={cookieSettings.necessary}
                disabled={true}
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="necessary" className="font-medium text-gray-800">Notwendige Cookies</label>
              <p className="text-gray-600 text-sm mt-1">
                Diese Cookies sind für das Funktionieren der Website unerlässlich und können nicht deaktiviert werden.
              </p>
            </div>
          </div>
          
          {/* Funktionale Cookies */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="functional"
                name="functional"
                type="checkbox"
                checked={cookieSettings.functional}
                onChange={handleChange}
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="functional" className="font-medium text-gray-800 cursor-pointer">Funktionale Cookies</label>
              <p className="text-gray-600 text-sm mt-1">
                Diese Cookies ermöglichen erweiterte Funktionalitäten und Personalisierung auf unserer Website.
              </p>
            </div>
          </div>
          
          {/* Analyse-Cookies */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="analytics"
                name="analytics"
                type="checkbox"
                checked={cookieSettings.analytics}
                onChange={handleChange}
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="analytics" className="font-medium text-gray-800 cursor-pointer">Analyse-Cookies</label>
              <p className="text-gray-600 text-sm mt-1">
                Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, 
                indem sie Informationen anonym sammeln und melden.
              </p>
            </div>
          </div>
          
          {/* Marketing-Cookies */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="marketing"
                name="marketing"
                type="checkbox"
                checked={cookieSettings.marketing}
                onChange={handleChange}
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="marketing" className="font-medium text-gray-800 cursor-pointer">Marketing-Cookies</label>
              <p className="text-gray-600 text-sm mt-1">
                Diese Cookies werden verwendet, um Besuchern auf Websites Werbeanzeigen anzuzeigen, 
                die für sie relevant und ansprechend sind.
              </p>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
          <button
            onClick={rejectAll}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Alle ablehnen
          </button>
          <button
            onClick={saveSettings}
            className="px-4 py-2 border border-primary bg-white text-primary rounded-md hover:bg-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Einstellungen speichern
          </button>
          <button
            onClick={acceptAll}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentForm; 
